import { useState, useEffect } from 'react'
import { Equipment, User } from '@/types'

interface Filters {
  status: string[]
  priority: string[]
  assignedTo: string[]
  equipmentType: string[]
}

interface WorkOrderFiltersProps {
  filters: Filters
  onApplyFilters: (filters: Filters) => void
  equipment: Equipment[]
  users: User[]
}

export default function WorkOrderFilters({ filters, onApplyFilters, equipment, users }: WorkOrderFiltersProps) {
  const [localFilters, setLocalFilters] = useState<Filters>(filters)

  useEffect(() => {
    setLocalFilters(filters)
  }, [filters])

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'on-hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ]

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ]

  const equipmentTypes = [...new Set(equipment.map(item => item.type))].map(type => ({
    value: type,
    label: type.charAt(0).toUpperCase() + type.slice(1)
  }))

  const technicians = users.filter(user => user.role === 'technician' || user.role === 'supervisor')

  const handleFilterChange = (filterType: keyof Filters, value: string, checked: boolean) => {
    setLocalFilters(prev => ({
      ...prev,
      [filterType]: checked
        ? [...prev[filterType], value]
        : prev[filterType].filter(item => item !== value)
    }))
  }

  const handleApply = () => {
    onApplyFilters(localFilters)
  }

  const handleClear = () => {
    const clearedFilters = {
      status: [],
      priority: [],
      assignedTo: [],
      equipmentType: []
    }
    setLocalFilters(clearedFilters)
    onApplyFilters(clearedFilters)
  }

  return (
    <div className="bg-gray-50 p-4 rounded-lg space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Status Filter */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Status</h4>
          <div className="space-y-2">
            {statusOptions.map(option => (
              <label key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={localFilters.status.includes(option.value)}
                  onChange={(e) => handleFilterChange('status', option.value, e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Priority Filter */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Priority</h4>
          <div className="space-y-2">
            {priorityOptions.map(option => (
              <label key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={localFilters.priority.includes(option.value)}
                  onChange={(e) => handleFilterChange('priority', option.value, e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Equipment Type Filter */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Equipment Type</h4>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {equipmentTypes.map(option => (
              <label key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={localFilters.equipmentType.includes(option.value)}
                  onChange={(e) => handleFilterChange('equipmentType', option.value, e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Assigned To Filter */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Assigned To</h4>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={localFilters.assignedTo.includes('unassigned')}
                onChange={(e) => handleFilterChange('assignedTo', 'unassigned', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Unassigned</span>
            </label>
            {technicians.map(user => (
              <label key={user.id} className="flex items-center">
                <input
                  type="checkbox"
                  checked={localFilters.assignedTo.includes(user.id)}
                  onChange={(e) => handleFilterChange('assignedTo', user.id, e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {user.name || `${user.first_name} ${user.last_name}`}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          onClick={handleClear}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Clear All
        </button>
        <button
          onClick={handleApply}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Apply Filters
        </button>
      </div>
    </div>
  )
}